import isomorphic_fetch from 'isomorphic-fetch';
import HttpRequestBuilder, { type HttpRequest } from './httpRequestBuilder';

export interface ApiResponse<T> {
  successful: boolean;
  response: T;
  message?: string;
}

export class RequestError extends Error {
  public static create = (response: Response): Promise<RequestError> =>
    new Promise((resolve) => {
      response
        .json()
        .then((content: any) => {
          resolve(new RequestError(response, content));
        })
        .catch(() => {
          resolve(new RequestError(response, null));
        });
    });

  public content: any;

  public statusCode: number;

  private constructor(response: Response, content: any) {
    super(String(response.status));
    this.statusCode = response.status;
    this.content = content;
  }
}
const httpStatusCheck = async (response: Response): Promise<Response> => {
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response);
  }

  const error = await RequestError.create(response);
  throw error;
};

export const raw = async (url: string, options: HttpRequest) => {
  const response = await isomorphic_fetch(url, options);
  const _response = await httpStatusCheck(response);
  return _response;
};

export const validateResponse = <T>(response: ApiResponse<T>): T => {
  if (!response || !response.successful) {
    throw new Error('The request did not succeed');
  }

  return response.response;
};

const noContentCodes: string[] = ['202', '204'];
export const json = <T>(url: string, options: RequestInit): Promise<ApiResponse<T>> =>
  raw(url, options).then((response: Response) => {
    if (noContentCodes.includes(String(response.status))) return null;
    return response.json();
  });

export const httpJson = async <T>(url: string, options: HttpRequest): Promise<T> => {
  const response = await raw(url, options);
  if (noContentCodes.includes(String(response.status))) {
    return Promise.resolve(null as unknown as T);
  }

  const content = (await response.json()) as T;

  return content;
};

export const builder = (): HttpRequestBuilder => new HttpRequestBuilder();
